import { 
  Component, 
  Input, 
  Output, 
  EventEmitter, 
  OnInit, 
  OnDestroy, 
  OnChanges,
  ViewChild, 
  ElementRef, 
  ChangeDetectionStrategy, 
  ChangeDetectorRef,
  AfterViewInit
} from '@angular/core';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateService } from '@ngx-translate/core';

// Declaração global do ACE Editor
declare const ace: any;

export interface JsonValidationResult {
  isValid: boolean;
  error?: string;
  line?: number;
  column?: number;
}

@Component({
  selector: 'app-json-editor-beta',
  templateUrl: './json-editor.component.html',
  styleUrls: ['./json-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class JsonEditorBetaComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('editor', { static: true }) editorRef!: ElementRef;
  
  @Input() jsonData: any = {};
  @Input() readOnly = false;
  @Input() height = '400px';
  @Input() theme = 'github';
  @Input() showValidation = true;
  @Input() autoFormat = true;
  @Input() debounceTime = 300;

  @Output() jsonChange = new EventEmitter<any>();
  @Output() validationChange = new EventEmitter<JsonValidationResult>();
  @Output() editorReady = new EventEmitter<any>();

  private editor: any;
  private destroy$ = new Subject<void>();
  private jsonString$ = new Subject<string>();
  private isInitialized = false;

  public validationResult: JsonValidationResult = { isValid: true };
  public isEditorMode = false;
  public isLoading = true;

  constructor(
    private message: NzMessageService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Configura debounce para mudanças no JSON
    this.jsonString$
      .pipe(
        debounceTime(this.debounceTime),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(jsonString => {
        this.processJsonChange(jsonString);
      });
  }

  ngAfterViewInit() {
    this.initializeEditor();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.editor) {
      this.editor.destroy();
    }
  }

  private async initializeEditor() {
    try {
      // Carrega ACE Editor se não estiver disponível
      if (typeof (window as any).ace === 'undefined') {
        await this.loadAceEditor();
      }

      const aceGlobal = (window as any).ace;
      this.editor = aceGlobal.edit(this.editorRef.nativeElement);
      
      // Configurações do editor
      this.editor.setTheme(`ace/theme/${this.theme}`);
      this.editor.session.setMode('ace/mode/json');
      this.editor.session.setTabSize(2);
      this.editor.session.setUseWrapMode(true);
      this.editor.setReadOnly(this.readOnly);
      
      this.editor.setOptions({
        fontSize: '14px',
        showPrintMargin: false,
        highlightActiveLine: !this.readOnly,
        showGutter: true,
        enableBasicAutocompletion: true,
        enableLiveAutocompletion: true,
        enableSnippets: true
      });

      // Define o conteúdo inicial
      this.updateEditorContent();

      // Configura eventos
      this.setupEditorEvents();

      this.isInitialized = true;
      this.isLoading = false;
      
      // Carrega conteúdo inicial após inicialização
      this.updateEditorContent();
      
      this.editorReady.emit(this.editor);
      this.cdr.markForCheck();

    } catch (error) {
      console.error('Erro ao inicializar editor JSON:', error);
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  private async loadAceEditor() {
    return new Promise<void>((resolve, reject) => {
      if (typeof (window as any).ace !== 'undefined') {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'assets/ace/ace.js';
      script.onload = () => {
        // Carrega módulos necessários
        try {
          const aceGlobal = (window as any).ace;
          if (aceGlobal && aceGlobal.config) {
            aceGlobal.config.set('basePath', 'assets/ace');
            aceGlobal.config.setModuleUrl('ace/mode/json_worker', 'assets/ace/worker-json.js');
          }
        } catch (error) {
          console.warn('Erro ao configurar ACE Editor:', error);
        }
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  private setupEditorEvents() {
    if (!this.editor || this.readOnly) return;

    // Evento de mudança no conteúdo
    this.editor.session.on('change', () => {
      const content = this.editor.getValue();
      this.jsonString$.next(content);
    });

    // Evento de foco
    this.editor.on('focus', () => {
      this.isEditorMode = true;
      this.cdr.markForCheck();
    });

    // Evento de perda de foco
    this.editor.on('blur', () => {
      this.isEditorMode = false;
      this.cdr.markForCheck();
    });
  }

  private processJsonChange(jsonString: string) {
    const validation = this.validateJson(jsonString);
    this.validationResult = validation;
    this.validationChange.emit(validation);

    if (validation.isValid) {
      try {
        const parsedJson = JSON.parse(jsonString);
        this.jsonChange.emit(parsedJson);
      } catch (error) {
        // Erro já tratado na validação
      }
    }

    this.cdr.markForCheck();
  }

  private validateJson(jsonString: string): JsonValidationResult {
    if (!jsonString.trim()) {
      return { isValid: true };
    }

    try {
      JSON.parse(jsonString);
      return { isValid: true };
    } catch (error: any) {
      const match = error.message.match(/at position (\d+)/);
      let line = 1;
      let column = 1;

      if (match) {
        const position = parseInt(match[1]);
        const lines = jsonString.substring(0, position).split('\n');
        line = lines.length;
        column = lines[lines.length - 1].length + 1;
      }

      return {
        isValid: false,
        error: error.message,
        line,
        column
      };
    }
  }

  private updateEditorContent() {
    if (!this.editor || !this.isInitialized) return;

    try {
      let jsonString: string;
      
      if (typeof this.jsonData === 'string') {
        jsonString = this.jsonData;
      } else {
        jsonString = JSON.stringify(this.jsonData, null, 2);
      }

      // Evita loop infinito de mudanças
      if (this.editor.getValue() !== jsonString) {
        this.editor.setValue(jsonString);
        this.editor.clearSelection();
        
        if (this.autoFormat && !this.readOnly) {
          this.formatJson();
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar conteúdo do editor:', error);
    }
  }

  // Métodos públicos
  public formatJson() {
    if (!this.editor || this.readOnly) return;

    try {
      const content = this.editor.getValue();
      const parsed = JSON.parse(content);
      const formatted = JSON.stringify(parsed, null, 2);
      
      this.editor.setValue(formatted);
      this.editor.clearSelection();
      
      this.translate.get('builderScreen.jsonEditor.formatted')
        .subscribe(msg => this.message.success(msg));
    } catch (error) {
      this.translate.get('builderScreen.jsonEditor.formatError')
        .subscribe(msg => this.message.error(msg));
    }
  }

  public validateCurrentJson(): JsonValidationResult {
    const content = this.editor ? this.editor.getValue() : '';
    return this.validateJson(content);
  }

  public setReadOnly(readOnly: boolean) {
    this.readOnly = readOnly;
    if (this.editor) {
      this.editor.setReadOnly(readOnly);
    }
  }

  public focus() {
    if (this.editor) {
      this.editor.focus();
    }
  }

  public getEditorContent(): string {
    return this.editor ? this.editor.getValue() : '';
  }

  public setEditorContent(content: string) {
    if (this.editor) {
      this.editor.setValue(content);
      this.editor.clearSelection();
    }
  }

  public forceUpdate(jsonData: any) {
    this.jsonData = jsonData;
    this.updateEditorContent();
  }

  public copyToClipboard() {
    const content = this.getEditorContent();
    navigator.clipboard.writeText(content).then(() => {
      this.translate.get('builderScreen.json.copied')
        .subscribe(msg => this.message.success(msg));
    }).catch(() => {
      this.translate.get('builderScreen.json.copyFailed')
        .subscribe(msg => this.message.error(msg));
    });
  }

  // Métodos auxiliares para o template
  public getValidationErrorDescription(): string {
    if (!this.validationResult.error) return '';
    
    let description = this.validationResult.error;
    if (this.validationResult.line && this.validationResult.column) {
      description += ` (Linha ${this.validationResult.line}, Coluna ${this.validationResult.column})`;
    }
    return description;
  }

  public getLineCount(): number {
    if (!this.editor) return 0;
    return this.editor.session.getLength();
  }

  public getCharacterCount(): number {
    if (!this.editor) return 0;
    return this.editor.getValue().length;
  }

  // Atualiza quando jsonData muda externamente
  ngOnChanges(changes: any) {
    if (this.isInitialized && changes?.jsonData) {
      this.updateEditorContent();
    }
  }
}
